import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminShell from "@/pages/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronRight,
  Search,
  Users,
  Scan,
} from "lucide-react";

const STATUS_META = {
  submitted: { label: "Pending", color: "bg-[#B58957]/20 text-[#8A4632]", Icon: Clock },
  approved: { label: "Approved", color: "bg-[#264C39]/10 text-[#264C39]", Icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-[#992F2F]/10 text-[#992F2F]", Icon: XCircle },
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState("submitted");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [autoSettings, setAutoSettings] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/settings/kyc-auto-approve").then(({ data }) => setAutoSettings(data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user && user !== false && user.role !== "admin") {
      // non-admin user — bounce
    }
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/admin/kyc?status=${status}&limit=100`),
      api.get(`/admin/stats`),
    ]).then(([list, s]) => {
      if (cancelled) return;
      setItems(list.data.items || []);
      setStats(s.data);
      setLoading(false);
    }).catch((e) => { if (!cancelled) { setLoading(false); console.error(e); } });
    return () => { cancelled = true; };
  }, [user, status]);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center text-[#4F4D45]">Verifying access…</div>;
  }
  if (user === false) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (user.role !== "admin") {
    return <Navigate to="/app" replace />;
  }

  const filtered = items.filter((i) =>
    !search
      ? true
      : (i.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (i.email || "").toLowerCase().includes(search.toLowerCase()) ||
        (i.wallet_id || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminShell>
      <div className="text-eyebrow text-[#4F4D45]">Compliance</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">KYC review queue</h1>

      {/* Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-stats">
          <StatCard label="Pending review" value={stats?.kyc_submitted} accent="text-[#8A4632]" Icon={Clock} />
          <StatCard label="Approved" value={stats?.kyc_approved} accent="text-[#264C39]" Icon={CheckCircle2} />
          <StatCard label="Rejected" value={stats?.kyc_rejected} accent="text-[#992F2F]" Icon={XCircle} />
          <StatCard label="Total users" value={stats?.users_total} accent="text-[#0A0A08]" Icon={Users} />
          <StatCard label="Transactions" value={stats?.transactions_total} accent="text-[#0A0A08]" Icon={ShieldCheck} />
        </div>

        {/* Auto-approve settings */}
        {autoSettings && (
          <div className="mt-6 fv-card p-5" data-testid="admin-auto-approve-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1B3526]/10 text-[#1B3526] flex items-center justify-center">
                  <Scan className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-medium">KYC auto-approve</div>
                  <div className="text-xs text-[#4F4D45]">Personal accounts whose face match clears the threshold get approved automatically. Business accounts always need manual review.</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#4F4D45]" data-testid="admin-auto-approve-state">{autoSettings.enabled ? "Enabled" : "Disabled"}</span>
                <Switch
                  checked={autoSettings.enabled}
                  onCheckedChange={(v) => setAutoSettings((s) => ({ ...s, enabled: v }))}
                  data-testid="admin-auto-approve-toggle"
                />
              </div>
            </div>
            <div className={`mt-5 grid sm:grid-cols-2 gap-5 ${autoSettings.enabled ? "" : "opacity-50 pointer-events-none"}`}>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-eyebrow text-[#4F4D45]">Similarity threshold</label>
                  <span className="font-mono text-sm" data-testid="admin-auto-approve-threshold-value">{autoSettings.threshold.toFixed(1)}%</span>
                </div>
                <Slider
                  min={70}
                  max={100}
                  step={0.5}
                  value={[autoSettings.threshold]}
                  onValueChange={(v) => setAutoSettings((s) => ({ ...s, threshold: v[0] }))}
                  data-testid="admin-auto-approve-threshold"
                />
                <div className="mt-1 text-[10px] text-[#4F4D45] flex justify-between">
                  <span>70%</span><span>Recommended ≥ 95%</span><span>100%</span>
                </div>
              </div>
              <div>
                <div className="text-eyebrow text-[#4F4D45] mb-2">Liveness check</div>
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <Switch
                    checked={autoSettings.require_liveness}
                    onCheckedChange={(v) => setAutoSettings((s) => ({ ...s, require_liveness: v }))}
                    data-testid="admin-auto-approve-liveness"
                  />
                  <span>Require liveness passed to auto-approve</span>
                </label>
                <div className="mt-2 text-xs text-[#4F4D45] leading-relaxed">Recommended on. Stops spoofing attacks where the photo on the ID is reused as the selfie.</div>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <Button
                onClick={async () => {
                  setAutoSaving(true);
                  try {
                    const { data } = await api.patch("/admin/settings/kyc-auto-approve", autoSettings);
                    setAutoSettings(data);
                    toast.success("Auto-approve settings saved.");
                  } catch (e) { toast.error("Save failed."); }
                  finally { setAutoSaving(false); }
                }}
                disabled={autoSaving}
                className="bg-[#1B3526] hover:bg-[#0F2517]"
                data-testid="admin-auto-approve-save"
              >
                {autoSaving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Tabs value={status} onValueChange={setStatus} className="w-full sm:w-auto" data-testid="admin-status-tabs">
            <TabsList className="bg-[#E3DECF]">
              <TabsTrigger value="submitted" data-testid="admin-tab-submitted">Pending</TabsTrigger>
              <TabsTrigger value="approved" data-testid="admin-tab-approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected" data-testid="admin-tab-rejected">Rejected</TabsTrigger>
              <TabsTrigger value="all" data-testid="admin-tab-all">All</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4F4D45]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or wallet…"
              data-testid="admin-search"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#D2CDBE] bg-white text-sm focus:border-[#1B3526] focus:outline-none"
            />
          </div>
        </div>

        {/* List */}
        <div className="mt-6 fv-card overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-[#4F4D45]">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[#4F4D45]" data-testid="admin-empty">No accounts in this view.</div>
          ) : (
            <ul className="divide-y divide-[#D2CDBE]" data-testid="admin-kyc-list">
              {filtered.map((u) => {
                const meta = STATUS_META[u.kyc_status] || STATUS_META.submitted;
                const Icon = meta.Icon;
                return (
                  <li key={u.id} className="hover:bg-[#F0ECE0]/60">
                    <Link to={`/admin/kyc/${u.id}`} className="flex items-center gap-4 px-5 py-4" data-testid={`admin-kyc-item-${u.id}`}>
                      <div className="w-10 h-10 rounded-full bg-[#1B3526]/10 text-[#1B3526] flex items-center justify-center font-display font-semibold text-sm">
                        {(u.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{u.name} <span className="text-[#4F4D45] font-normal">· {u.account_type}</span></div>
                        <div className="text-xs text-[#4F4D45] truncate">{u.email} · <span className="font-mono">{u.wallet_id}</span></div>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 ${meta.color}`}>
                        <Icon className="w-3.5 h-3.5" /> {meta.label}
                      </span>
                      <ChevronRight className="w-4 h-4 text-[#4F4D45]" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
    </AdminShell>
  );
}

function StatCard({ label, value, accent, Icon }) {
  return (
    <div className="fv-card p-4">
      <div className="flex items-center justify-between">
        <div className="text-eyebrow text-[#4F4D45]">{label}</div>
        <Icon className={`w-4 h-4 ${accent}`} strokeWidth={1.5} />
      </div>
      <div className={`font-display text-2xl font-semibold mt-1 ${accent}`}>{value ?? "—"}</div>
    </div>
  );
}
